import { useState, useRef, useCallback, useEffect } from "react";
import { motion, AnimatePresence } from "framer-motion";
import { ChevronLeft, ChevronRight } from "lucide-react";
import { Reveal } from "@/components/Reveal";
import { useLang } from "@/i18n";

const PAIRS = [
  {
    id: 1,
    label: "Präparation & Aufbau",
    caption: "Von rohem Naturnagel zum makellosen, spiegelglatten Finish.",
    before: "/before-after/vorher_1.jpg",
    after: "/before-after/nachher_1.jpg",
  },
  {
    id: 2,
    label: "French Design",
    caption: "Vom natürlichen Wachstum zur eleganten French-Verlängerung mit Akzent.",
    before: "/before-after/vorher_2.jpg",
    after: "/before-after/nachher_2.jpg",
  },
  {
    id: 3,
    label: "Pediküre French",
    caption: "Weiche, gepflegte Zehen mit klarer French-Linie und feinem Shimmer.",
    before: "/before-after/vorher_3.jpg",
    after: "/before-after/nachher_3.jpg",
    objectPosition: "center 70%",
  },
];

const Slider = ({ before, after, objectPosition }) => {
  const [pos, setPos] = useState(50);
  const [dragging, setDragging] = useState(false);
  const wrapRef = useRef(null);

  const updateFromClientX = useCallback((clientX) => {
    const el = wrapRef.current;
    if (!el) return;
    const rect = el.getBoundingClientRect();
    const x = clientX - rect.left;
    const pct = Math.max(0, Math.min(100, (x / rect.width) * 100));
    setPos(pct);
  }, []);

  useEffect(() => {
    if (!dragging) return;
    const move = (e) => {
      const x = e.touches ? e.touches[0].clientX : e.clientX;
      updateFromClientX(x);
    };
    const stop = () => setDragging(false);
    window.addEventListener("mousemove", move);
    window.addEventListener("touchmove", move, { passive: true });
    window.addEventListener("mouseup", stop);
    window.addEventListener("touchend", stop);
    return () => {
      window.removeEventListener("mousemove", move);
      window.removeEventListener("touchmove", move);
      window.removeEventListener("mouseup", stop);
      window.removeEventListener("touchend", stop);
    };
  }, [dragging, updateFromClientX]);

  return (
    <div
      ref={wrapRef}
      data-testid="ba-slider"
      className="relative w-full aspect-[4/5] md:aspect-[16/10] overflow-hidden select-none bg-muted cursor-ew-resize"
      onMouseDown={(e) => { setDragging(true); updateFromClientX(e.clientX); }}
      onTouchStart={(e) => { setDragging(true); updateFromClientX(e.touches[0].clientX); }}
    >
      {/* AFTER (full width, background) */}
      <img src={after} alt="Nachher" draggable={false} className="absolute inset-0 w-full h-full object-cover pointer-events-none" style={objectPosition ? { objectPosition } : undefined} />
      {/* BEFORE (clipped from right using clip-path — resize-safe, pure CSS) */}
      <img
        src={before}
        alt="Vorher"
        draggable={false}
        className="absolute inset-0 w-full h-full object-cover pointer-events-none"
        style={{ clipPath: `inset(0 ${100 - pos}% 0 0)`, ...(objectPosition ? { objectPosition } : {}) }}
      />

      {/* Labels */}
      <span data-testid="ba-label-before" className="absolute top-4 left-4 text-[10px] md:text-xs uppercase tracking-[0.25em] bg-background/85 backdrop-blur-sm text-foreground px-3 py-1.5">Vorher</span>
      <span data-testid="ba-label-after" className="absolute top-4 right-4 text-[10px] md:text-xs uppercase tracking-[0.25em] bg-primary/90 backdrop-blur-sm text-primary-foreground px-3 py-1.5">Nachher</span>

      {/* Divider line + handle */}
      <div
        className="absolute top-0 bottom-0 w-px bg-background/95 pointer-events-none"
        style={{ left: `${pos}%` }}
      >
        <div
          data-testid="ba-handle"
          className="absolute top-1/2 -translate-y-1/2 -translate-x-1/2 h-12 w-12 rounded-full bg-background shadow-[0_6px_24px_rgba(0,0,0,0.25)] flex items-center justify-center border border-border pointer-events-auto"
          style={{ left: 0, cursor: "ew-resize" }}
          onMouseDown={(e) => { e.stopPropagation(); setDragging(true); }}
          onTouchStart={(e) => { e.stopPropagation(); setDragging(true); }}
        >
          <ChevronLeft strokeWidth={1.5} size={16} className="text-foreground/70" />
          <ChevronRight strokeWidth={1.5} size={16} className="text-foreground/70" />
        </div>
      </div>
    </div>
  );
};

export const BeforeAfter = () => {
  const [idx, setIdx] = useState(0);
  const { t } = useLang();
  const pair = PAIRS[idx];
  const go = (delta) => setIdx((i) => (i + delta + PAIRS.length) % PAIRS.length);

  return (
    <section id="vorher-nachher" data-testid="before-after-section" className="bg-background border-t border-border">
      <div className="mx-auto max-w-[1400px] px-5 md:px-10 py-24 md:py-32">
        <Reveal>
          <div className="flex items-end justify-between flex-wrap gap-6 mb-12 md:mb-16">
            <div>
              <span className="text-xs uppercase tracking-[0.25em] text-muted-foreground">{t("beforeafter.eyebrow")}</span>
              <h2 className="font-serif font-light text-4xl md:text-6xl tracking-tight mt-4">
                {t("beforeafter.title")}
              </h2>
              <p className="mt-5 max-w-md text-foreground/60 leading-relaxed">
                {t("beforeafter.body")}
              </p>
            </div>
            <div className="flex items-center gap-3" data-testid="ba-nav">
              <button
                type="button"
                onClick={() => go(-1)}
                data-testid="ba-prev"
                aria-label="Vorheriges Paar"
                className="h-11 w-11 border border-border hover:border-primary hover:text-primary transition-colors flex items-center justify-center"
              >
                <ChevronLeft strokeWidth={1.5} size={18} />
              </button>
              <span className="text-xs tracking-[0.2em] text-muted-foreground tabular-nums" data-testid="ba-counter">
                {String(idx + 1).padStart(2, "0")} / {String(PAIRS.length).padStart(2, "0")}
              </span>
              <button
                type="button"
                onClick={() => go(1)}
                data-testid="ba-next"
                aria-label="Nächstes Paar"
                className="h-11 w-11 border border-border hover:border-primary hover:text-primary transition-colors flex items-center justify-center"
              >
                <ChevronRight strokeWidth={1.5} size={18} />
              </button>
            </div>
          </div>
        </Reveal>

        <Reveal delay={0.1}>
          <AnimatePresence mode="wait">
            <motion.div
              key={pair.id}
              initial={{ opacity: 0, y: 12 }}
              animate={{ opacity: 1, y: 0 }}
              exit={{ opacity: 0, y: -12 }}
              transition={{ duration: 0.45, ease: [0.22, 1, 0.36, 1] }}
              className="grid md:grid-cols-12 gap-8 md:gap-10 items-end"
            >
              <div className="md:col-span-9">
                <Slider before={pair.before} after={pair.after} objectPosition={pair.objectPosition} />
              </div>
              <div className="md:col-span-3">
                <span className="text-xs uppercase tracking-[0.25em] text-primary">{pair.label}</span>
                <p className="mt-4 font-serif text-2xl md:text-3xl font-light leading-tight text-foreground/85">
                  {pair.caption}
                </p>
              </div>
            </motion.div>
          </AnimatePresence>
        </Reveal>
      </div>
    </section>
  );
};
